import { useCallback, useMemo, useRef } from "react";
import { RenderContent } from "./render-content";
import { FileUploadState } from "..";

const ACCEPTED_FILE_TYPES = ".csv";

interface FileUploadZoneProps {
  state: FileUploadState;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  maxFileSize: number;
  isUploading: boolean;
  errorMessage?: string;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  state,
  onFileSelect,
  onFileRemove,
  maxFileSize,
  isUploading,
  errorMessage,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [onFileSelect],
  );

  const handleClick = useCallback(() => {
    if (!isUploading) {
      inputRef.current?.click();
    }
  }, [isUploading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const getZoneStyles = useMemo(() => {
    const baseStyles =
      "border-2 border-dashed rounded-lg flex flex-col items-center justify-center py-8 transition-all duration-200 min-h-[160px]";

    switch (state.status) {
      case "success":
        return `${baseStyles} border-green-500 bg-green-50 hover:bg-green-100`;
      case "error":
        return `${baseStyles} border-red-500 bg-red-50 hover:bg-red-100`;
      case "uploading":
        return `${baseStyles} border-blue-500 bg-blue-50 cursor-not-allowed`;
      default:
        return `${baseStyles} border-gray-300 bg-gray-50 hover:border-primary hover:bg-gray-100 cursor-pointer`;
    }
  }, [state.status]);

  return (
    <div className="space-y-2">
      <div
        className={getZoneStyles}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={isUploading ? -1 : 0}
        role="button"
        aria-label={
          isUploading ? "Enviando arquivo..." : "Selecionar arquivo CSV"
        }
        aria-disabled={isUploading}
      >
        <RenderContent
          state={state}
          onFileRemove={onFileRemove}
          errorMessage={errorMessage || ""}
          maxFileSize={maxFileSize}
        />
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        style={{ display: "none" }}
        onChange={handleFileChange}
        disabled={isUploading}
        aria-label="Selecionar arquivo CSV"
      />
    </div>
  );
};
